import * as yup from 'yup';

import { AnnualReportNavigationDirection, AnnualReportProps } from '../AnnualReport';
import { Card, CardBody, CardFooter, Col, Form, FormGroup, Input, Label, Row } from 'reactstrap';
import { ProgramSummativeMeasure, ProgramSummativeMeasureDetail } from '../../../common/Types';
import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from '../../../components';
import Skeleton from 'react-loading-skeleton';
import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';

const SummativeMeasures = (props: AnnualReportProps) => {
    const [summativeMeasure, setSummativeMeasure] = useState<ProgramSummativeMeasure>();
    const [formSubmitting, setFormSubmitting] = useState<boolean>(false);
    const [submitDirection, setSubmitDirection] = useState<AnnualReportNavigationDirection>(
        AnnualReportNavigationDirection.Forward,
    );

    const programSummativeMeasureSchema = yup
        .object({
            summativeMeasuresId: yup.number().required().default(0),
            anyNo: yup.boolean(),
            programId: yup.number().required().default(props.program.programId),
            activeYear: yup.number().required().default(+props.reportYear),
            descriptionAnalysis: yup.string().notRequired().nullable(),
            actionPlan: yup.string().notRequired().nullable(),
            createdBy: yup.string().notRequired().nullable(),
            dateCreated: yup.date().notRequired().nullable().default(new Date()),
            lastEdited: yup.date().notRequired().nullable().default(new Date()),
            editedBy: yup.string().notRequired().nullable(),
            createdByString: yup.string().notRequired().nullable(),
            editedByString: yup.string().notRequired().nullable(),
            programSummativeMeasureDetailDto: yup
                .array()
                .of(
                    yup
                        .object<ProgramSummativeMeasureDetail>({
                            id: yup.number().required().default(0),
                            summativeMeasuresId: yup.number().required().default(0),
                            title: yup.string().required(),
                            status: yup
                                .boolean()
                                .typeError('Please select Yes or No.')
                                .required('Please select Yes or No.'),
                        })
                        .defined(),
                )
                .default([]),
        })
        .defined();

    const {
        reset,
        control,
        handleSubmit,
        watch,
        setValue,
        getValues,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(programSummativeMeasureSchema as any),
    });
    //setFormSubmitting(isSubmitting);
    const { fields } = useFieldArray({
        control: control,
        name: 'programSummativeMeasureDetailDto',
    });

    useEffect(() => {
        const loadSummativeMeasure = () => {
            programService
                .getSummativeMeasure(props.program.programId, props.profession.professionId, props.reportYear)
                .then((result) => {
                    setSummativeMeasure(result);
                    reset(result);
                })
                .catch(() => {
                    toast.error('Error loading summative measures');
                });
        };

        loadSummativeMeasure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.program.programId, props.profession.professionId, props.reportYear]);

    const submitForm = async (values) => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving summative measures...');

        programService
            .saveSummativeMeasure(values)
            .then((result) => {
                // Update form with server response (especially summativeMeasuresId for new records)
                setSummativeMeasure(result);
                reset(result);
                toast.update(toastId, {
                    type: 'success',
                    render: 'Summative Measure saved successfully',
                });

                if (submitDirection === AnnualReportNavigationDirection.Forward) {
                    props.onSavedSuccessfully(AnnualReportNavigationDirection.Forward);
                }
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Summative Measures.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };

    const submitWithoutValidation = () => {
        setFormSubmitting(true);
        const toastId = toast.info('Saving summative measures...');

        const formValues = getValues();

        let values: ProgramSummativeMeasure;

        if (summativeMeasure?.summativeMeasuresId === 0) {
            values = {
                summativeMeasuresId: 0,
                programId: props.program.programId,
                activeYear: +props.reportYear,
                programSummativeMeasureDetailDto: formValues.programSummativeMeasureDetailDto,
                actionPlan: formValues.actionPlan,
                descriptionAnalysis: formValues.descriptionAnalysis,
            } as unknown as ProgramSummativeMeasure;
        } else {
            values = formValues as ProgramSummativeMeasure;
        }

        programService
            .saveSummativeMeasure(values)
            .then((result) => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Summative Measure saved successfully',
                });

                setSummativeMeasure(result);
                reset(result);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'Error saving Summative Measures.',
                });
            })
            .finally(() => {
                setFormSubmitting(false);
            });
    };
    const answers = watch('programSummativeMeasureDetailDto') as ProgramSummativeMeasureDetail[];
    const anyNo = answers && answers.filter((a) => a.status?.toString() === 'false').length > 0;
    setValue('anyNo', anyNo);

    return (
        <>
            <Row>
                <Col>
                    <h4 className={'card-title'}>Summative Measures</h4>
                    <h5 className={'card-subtitle text-muted mb-3'}>
                        Please enter yes or no for each option below. If you answer No for any item, please provide the
                        necessary analysis and action plan
                    </h5>
                </Col>
            </Row>

            <Form noValidate onSubmit={handleSubmit(submitForm, (validationErrors) => {
                const fieldNames = Object.keys(validationErrors);
                toast.error(`Please fix the following fields: ${fieldNames.join(', ')}`);
            })}>
                <Controller
                    name="anyNo"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="summativeMeasuresId"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                />
                <Controller
                    name="programId"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? props.program.programId} />}
                />
                <Controller
                    name="activeYear"
                    control={control}
                    render={({ field }) => <input type="hidden" {...field} value={field.value ?? +props.reportYear} />}
                />
                {(summativeMeasure?.summativeMeasuresId || 0) > 0 && (
                    <>
                        <Controller
                            name="createdBy"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="dateCreated"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="lastEdited"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="editedBy"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="createdByString"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <Controller
                            name="editedByString"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                    </>
                )}
                <Card>
                    <CardBody>
                        {!summativeMeasure && <Skeleton count={20} />}
                        {summativeMeasure && (
                            <>
                                <Row className={'mb-3'}>
                                    <Col>
                                        <span>Does the program document student competency in the following?</span>
                                    </Col>
                                </Row>
                                <div className={'d-flex flex-column'}>
                                    {fields &&
                                        fields.map((measure, i) => (
                                            <div className={'d-flex justify-content-between mb-2'} key={i}>
                                                <Controller
                                                    name={`programSummativeMeasureDetailDto[${i}].title` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).title}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`programSummativeMeasureDetailDto[${i}].id` as any}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).id}
                                                        />
                                                    )}
                                                />
                                                <Controller
                                                    name={`programSummativeMeasureDetailDto[${i}].summativeMeasuresId`}
                                                    control={control}
                                                    render={({ field }) => (
                                                        <input
                                                            type="hidden"
                                                            {...field}
                                                            value={field.value ?? (measure as any).summativeMeasuresId}
                                                        />
                                                    )}
                                                />
                                                <Label style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                    {(measure as any).title}
                                                </Label>
                                                <div
                                                    style={{ flexBasis: 'calc(50% - 10px)' }}
                                                    className={'d-flex flex-column'}
                                                >
                                                    <Controller
                                                        name={`programSummativeMeasureDetailDto[${i}].status`}
                                                        control={control}
                                                        render={({ field }) => (
                                                            <div className={'d-flex'}>
                                                                <div className={'custom-control custom-radio mx-3'}>
                                                                    <input
                                                                        type="radio"
                                                                        id={`radioYes${i}`}
                                                                        className="custom-control-input"
                                                                        name={field.name}
                                                                        checked={field.value === 'true' || field.value === true}
                                                                        value="true"
                                                                        onChange={() => field.onChange('true')}
                                                                    />
                                                                    <label
                                                                        className="custom-control-label"
                                                                        htmlFor={`radioYes${i}`}
                                                                    >
                                                                        Yes
                                                                    </label>
                                                                </div>
                                                                <div className={'custom-control custom-radio mx-3'}>
                                                                    <input
                                                                        type="radio"
                                                                        id={`radioNo${i}`}
                                                                        className="custom-control-input"
                                                                        name={field.name}
                                                                        checked={field.value === 'false' || field.value === false}
                                                                        value="false"
                                                                        onChange={() => field.onChange('false')}
                                                                    />
                                                                    <label
                                                                        className="custom-control-label"
                                                                        htmlFor={`radioNo${i}`}
                                                                    >
                                                                        No
                                                                    </label>
                                                                </div>
                                                            </div>
                                                        )}
                                                    />
                                                    {errors.programSummativeMeasureDetailDto &&
                                                        errors.programSummativeMeasureDetailDto[i] && (
                                                            <Label className={'text-danger'}>
                                                                {
                                                                    errors.programSummativeMeasureDetailDto[i]?.status
                                                                        ?.message as string
                                                                }
                                                            </Label>
                                                        )}
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                {anyNo && (
                                    <Row>
                                        <Col className={'d-flex justify-content-between'}>
                                            <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                <Label>{`Detailed Analysis of Summative Measure(s)`}</Label>
                                                <Controller
                                                    name="descriptionAnalysis"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="textarea"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            rows={10}
                                                        />
                                                    )}
                                                />
                                                {errors.descriptionAnalysis && (
                                                    <Label className={'text-danger'}>
                                                        {errors.descriptionAnalysis.message as string}
                                                    </Label>
                                                )}
                                            </FormGroup>
                                            <FormGroup style={{ flexBasis: 'calc(50% - 10px)' }}>
                                                <Label>{`Action Plan for Summative Measure(s)`}</Label>
                                                <Controller
                                                    name="actionPlan"
                                                    control={control}
                                                    render={({ field }) => (
                                                        <Input
                                                            type="textarea"
                                                            {...field}
                                                            value={field.value ?? ''}
                                                            rows={10}
                                                        />
                                                    )}
                                                />
                                                {errors.actionPlan && (
                                                    <Label className={'text-danger'}>
                                                        {errors.actionPlan.message as string}
                                                    </Label>
                                                )}
                                            </FormGroup>
                                        </Col>
                                    </Row>
                                )}
                            </>
                        )}
                    </CardBody>
                    {props.canEdit && (
                        <CardFooter className={'d-flex justify-content-between'}>
                            <Button
                                type={'button'}
                                color={'warning'}
                                onClick={() => props.onNavigate(AnnualReportNavigationDirection.Back)}
                            >
                                <i className={'mdi mdi-arrow-left'} />
                                <span className={'mr-1'}>{`Previous`}</span>
                            </Button>

                            <Button
                                disabled={formSubmitting}
                                type={'button'}
                                color={'primary'}
                                onClick={() => submitWithoutValidation()}
                            >
                                <i className={'mdi mdi-content-save'} />
                                <span className={'ml-1'}>{`Save`}</span>
                            </Button>

                            <Button
                                disabled={formSubmitting}
                                type={'submit'}
                                color={'info'}
                                onClick={() => setSubmitDirection(AnnualReportNavigationDirection.Forward)}
                            >
                                <span className={'mr-1'}>{`Save & Continue`}</span>
                                <i className={'mdi mdi-arrow-right'} />
                            </Button>
                        </CardFooter>
                    )}
                </Card>
            </Form>
        </>
    );
};

export default SummativeMeasures;
